// SourceSecretRevealModal — one-time reveal of the plaintext HMAC
//   secret returned by `POST /lead-sources` or `POST /lead-sources/
//   :code/rotate-secret`.
//
// @aiSecurity the secret value lives ONLY in this modal's local state.
//   It is NEVER cached in TanStack Query, persisted to localStorage,
//   echoed into the toast text, or written to a logger. Closing the
//   modal drops the reference; the backend will never return the same
//   value again — the user must copy it before closing.
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LeadSourceSecretReveal } from '@magimp/shared';
import { Button, EditorModal } from '../../../ui/index.js';
import { useToastStore } from '../../../stores/toast.js';

export interface SourceSecretRevealModalProps {
  open: boolean;
  reveal: LeadSourceSecretReveal | null;
  onClose: () => void;
}

export function SourceSecretRevealModal({ open, reveal, onClose }: SourceSecretRevealModalProps) {
  const { t } = useTranslation(['sources', 'common']);
  const show = useToastStore((s) => s.show);
  const [copied, setCopied] = useState(false);

  // WHY (#1080 F-1080-03): the modal is kept mounted by SourcesPage so
  //   create + rotate reuse it. Without resetting `copied`, a second
  //   reveal would inherit the prior copy acknowledgement and drop the
  //   unsaved-changes guard before the new one-time secret is safe.
  useEffect(() => {
    setCopied(false);
  }, [reveal?.secret, reveal?.source.code, reveal?.source.secretIssuedAt]);

  async function onCopy(): Promise<void> {
    if (!reveal) return;
    try {
      await navigator.clipboard.writeText(reveal.secret);
      setCopied(true);
      show({ tone: 'success', text: t('sources:reveal.copied') });
    } catch {
      // INVARIANT: clipboard write may fail in non-secure-context
      //   environments (e.g. test or older browsers). Fall back to a
      //   user-driven copy by selecting the input below; do not
      //   silently swallow.
      show({ tone: 'info', text: t('sources:reveal.copyFallback') });
    }
  }

  return (
    <EditorModal
      open={open && reveal != null}
      title={t('sources:reveal.title')}
      // INVARIANT (#1080): a revealed secret is irrecoverable. Mark the
      //   surface "dirty" until the user has acknowledged the copy
      //   step, so the unsaved-changes guard challenges accidental
      //   escapes/backdrop clicks.
      dirty={!copied}
      onConfirmClose={onClose}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button onClick={onClose} data-testid="source-secret-close">
            {copied ? t('common:actions.close') : t('sources:reveal.closeWithoutCopy')}
          </Button>
        </div>
      }
    >
      {reveal && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ fontSize: 13, color: 'var(--fg-2)', margin: 0 }}>
            {t('sources:reveal.warning')}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, color: 'var(--fg-2)' }}>{t('sources:reveal.source')}</span>
            <span style={{ fontSize: 14, fontWeight: 500 }}>
              {reveal.source.displayName}{' '}
              <span style={{ color: 'var(--fg-3)' }}>({reveal.source.code})</span>
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, color: 'var(--fg-2)' }}>{t('sources:reveal.secret')}</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                readOnly
                value={reveal.secret}
                data-testid="source-secret-value"
                onFocus={(e) => e.currentTarget.select()}
                style={{
                  flex: 1,
                  fontFamily: 'monospace',
                  fontSize: 13,
                  padding: '6px 8px',
                  border: '1px solid var(--border-1)',
                  borderRadius: 4,
                  background: 'var(--bg-2)',
                  color: 'var(--fg-1)',
                }}
              />
              <Button
                kind="primary"
                icon="copy"
                onClick={() => {
                  void onCopy();
                }}
                data-testid="source-secret-copy"
              >
                {copied ? t('sources:reveal.copiedShort') : t('sources:reveal.copy')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </EditorModal>
  );
}
